/**
 * Labstep
 *
 * @module components/Entity/Action/TransferOwnership
 * @desc Action to transfer ownership for an entity
 */

import { CanGroup } from 'labstep-web/components/Entity/Can';
import { FieldType } from 'labstep-web/constants/form';
import { TransferOwnershipContainer } from 'labstep-web/containers/TransferOwnership';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { FormReusable } from 'labstep-web/core/Form/Reusable';
import type { IFieldProps } from 'labstep-web/core/Form/Reusable/types';
import { ModalDefaultContainer as ModalDefault } from 'labstep-web/core/Modal/Default';
import { TextError } from 'labstep-web/core/Text/Error';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { Folder } from 'labstep-web/models/folder.model';
import { Group } from 'labstep-web/models/group.model';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import {
  USER_GROUP_TYPE_VIEW,
  type UserGroup,
} from 'labstep-web/models/user-group.model';
import { enhanceOptions } from 'labstep-web/services/utils.service';
import React from 'react';
import { object } from 'yup';
import type { EntityActionTransferOwnershipProps } from './types';

export const EntityActionTransferOwnership: React.FC<
  EntityActionTransferOwnershipProps
> = ({ entity, bulkIds, actionComponentProps, options = {} }) => {
  const [selectedGroupId, setSelectedGroupId] = React.useState<
    number | null
  >(null);

  const fieldGroup: IFieldProps = {
    fieldLabel: `Select ${Group.getHumanReadableEntityName()}`,
    name: 'group',
    validation: object().required(),
    fieldType: FieldType.SearchSelect,
    elementProps: {
      searchKey: 'search_query',
      entityName: 'user_group',
      params: {
        not_type: USER_GROUP_TYPE_VIEW,
        not_group_id: entity.owner.id,
        is_group_deleted: false,
      },
      setOptions: (userGroups: UserGroup[]) =>
        userGroups.map((userGroup: UserGroup) => userGroup.group),
    },
  };
  const fieldFolder: IFieldProps = React.useMemo(
    () => ({
      fieldLabel: `Select ${Folder.getHumanReadableEntityName()}`,
      name: 'folder',
      validation: object().required(),
      fieldType: FieldType.SearchSelect,
      elementProps: {
        searchKey: 'search_query',
        entityName: Folder.entityName,
        params: {
          group_id: selectedGroupId,
          type: entity.entityName,
        },
        creatable: true,
        createBody: {
          group_id: selectedGroupId,
          type: entity.entityName,
        },
      },
    }),
    [selectedGroupId],
  );

  const fieldWarning: IFieldProps = {
    fieldType: FieldType.action,
    component: () => (
      <TextError>
        {`WARNING: if you transfer ownership to a
                            ${Group.getHumanReadableEntityName()} you do not own you will no longer
                            have owner permissions.`}
      </TextError>
    ),
  };
  return (
    <CanGroup action="owner" groupId={entity.owner.id}>
      <TransferOwnershipContainer>
        {({ transferOwnership, status }): React.ReactElement => (
          <ModalDefault
            header="Transfer Ownership"
            inverted
            content={({ toggleModal }) => (
              <div>
                <FormReusable
                  color="red"
                  submitButtonText="Transfer Ownership"
                  onCancel={toggleModal}
                  status={status}
                  onSubmit={(values) => {
                    transferOwnership(
                      entity.entityName,
                      bulkIds || entity.id,
                      values.group_id as number,
                      values.folder_id as string | null | undefined,
                      enhanceOptions({
                        options,
                        onSuccess: toggleModal,
                        toast: true,
                      }),
                    );
                  }}
                  onFormChange={(values) => {
                    if (values.group_id) {
                      setSelectedGroupId(
                        values.group_id as number | null,
                      );
                    }
                  }}
                  fields={
                    (entity.entityName ===
                      ExperimentWorkflow.entityName ||
                      entity.entityName ===
                        ProtocolCollection.entityName) &&
                    selectedGroupId
                      ? [fieldGroup, fieldFolder, fieldWarning]
                      : [fieldGroup, fieldWarning]
                  }
                />
              </div>
            )}
            viewComponent={({ toggleModal }) => (
              <ActionComponent
                dataCy="components/Entity/Action/TransferOwnership"
                text="Transfer Ownership"
                type="option"
                icon="exchange"
                {...actionComponentProps}
                onClick={toggleModal}
              />
            )}
          />
        )}
      </TransferOwnershipContainer>
    </CanGroup>
  );
};
